import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CALCULATION_ASSUMPTIONS_EXAMPLE_INPUT,
  calculateFinancingVolume,
  calculationAssumptionsBodySchema,
  formatAmountInput,
  formatDate,
  formatDateTime,
  formatEuro,
  formatPercentInput,
  parseAmountInput,
  parsePercentInput,
  type CalculationAssumptions,
  type CalculationAssumptionsSaved,
  type CalculationAssumptionsVersion,
  type CalculationAssumptionsView,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Card,
  Dialog,
  Pill,
  Skeleton,
  Table,
  TextField,
  useToast,
  type TableColumn,
} from '../../ds/components';
import { ApiError, api, errorMessage } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { SettingsPageHeader } from './SettingsFrame';
import frameStyles from './SettingsFrame.module.css';
import styles from './CalculationAssumptionsSettingsPage.module.css';

/**
 * Einstellungen · Organisation „Rechenannahmen Selbstauskunft" (D-156, `GET/PUT
 * /settings/organization/calculation-assumptions`, `POST …/reset`): the five assumptions of the volume calculation
 * („Finanzierungsvolumen") and the twelve investment-calculator defaults („Immobilienrechner", D-277). Admins change
 * them (every save is a new version valid from now), the sales lead reads them. The example line is recomputed with
 * the shared formula while the owner types. No design frame exists; the texts are fixed by D-156 and D-277.
 */

const PATH = '/settings/organization/calculation-assumptions';
// Under the `organization` root: `organization.updated { section: 'assumptions' }` refetches the page.
const settingsKey = [...queryRoots.organization, 'calculation-assumptions'] as const;

const TITLE = 'Rechenannahmen Selbstauskunft';
const INTRO =
  'Diese Werte bestimmen das Finanzierungsvolumen, das die Selbstauskunft berechnet. Änderungen gelten für neue Berechnungen; bereits berechnete Selbstauskünfte behalten ihren Annahmen-Snapshot.';

type FieldKey = keyof CalculationAssumptions;
type FieldKind = 'amount' | 'percent' | 'integer';
type Section = 'volume' | 'calculator';

interface FieldSpec {
  key: FieldKey;
  label: string;
  suffix: string;
  kind: FieldKind;
  section: Section;
}

const FIELDS: readonly FieldSpec[] = [
  {
    key: 'livingExpensesCents',
    label: 'Lebenshaltungspauschale',
    suffix: '€ pro Monat',
    kind: 'amount',
    section: 'volume',
  },
  {
    key: 'rentCreditPct',
    label: 'Anrechnung Mieteinnahmen',
    suffix: '%',
    kind: 'percent',
    section: 'volume',
  },
  {
    key: 'capPct',
    label: 'Kappungsgrenze Kapitaldienst',
    suffix: '% des Einkommens',
    kind: 'percent',
    section: 'volume',
  },
  { key: 'interestPct', label: 'Kalkulationszins', suffix: '% p. a.', kind: 'percent', section: 'volume' },
  { key: 'repaymentPct', label: 'Tilgung', suffix: '% p. a.', kind: 'percent', section: 'volume' },
  { key: 'calcInterestPct', label: 'Sollzins', suffix: '% p. a.', kind: 'percent', section: 'calculator' },
  {
    key: 'calcRepaymentPct',
    label: 'Anfangstilgung',
    suffix: '% p. a.',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcFixedInterestYears',
    label: 'Zinsbindung',
    suffix: 'Jahre',
    kind: 'integer',
    section: 'calculator',
  },
  {
    key: 'calcFollowUpInterestPct',
    label: 'Anschlusszins',
    suffix: '% p. a.',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcFollowUpRepaymentPct',
    label: 'Anschlusstilgung',
    suffix: '% p. a.',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcRentIncreasePct',
    label: 'Mietsteigerung',
    suffix: '%',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcRentIncreaseIntervalMonths',
    label: 'Mietsteigerung alle N Monate',
    suffix: 'Monate',
    kind: 'integer',
    section: 'calculator',
  },
  {
    key: 'calcAppreciationPct',
    label: 'Wertentwicklung p.a.',
    suffix: '%',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcDepreciationPct',
    label: 'AfA linear',
    suffix: '% p. a.',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcTaxRatePct',
    label: 'Persönlicher Steuersatz (Vorgabe)',
    suffix: '%',
    kind: 'percent',
    section: 'calculator',
  },
  {
    key: 'calcReservePerSqmCents',
    label: 'Instandhaltungsrücklage',
    suffix: '€/m² mtl.',
    kind: 'amount',
    section: 'calculator',
  },
  {
    key: 'calcOtherCostsPerSqmCents',
    label: 'Sonstige Kosten',
    suffix: '€/m² mtl.',
    kind: 'amount',
    section: 'calculator',
  },
];

const SECTIONS: readonly { key: Section; title: string; intro: string | null }[] = [
  { key: 'volume', title: 'Finanzierungsvolumen', intro: null },
  {
    key: 'calculator',
    title: 'Immobilienrechner',
    intro:
      'Vorgaben für neue Berechnungen im Immobilienrechner, solange Einheit oder Kontakt keinen eigenen Wert haben.',
  },
];

const AMOUNT_MISSING = 'Bitte tragen Sie einen Betrag ein, zum Beispiel 900,00.';
const NUMBER_MISSING = 'Bitte tragen Sie eine Zahl ein, zum Beispiel 3,5.';
const INTEGER_MISSING = 'Bitte tragen Sie eine ganze Zahl ein, zum Beispiel 10.';

type Texts = Record<FieldKey, string>;

function textOf(field: FieldSpec, value: number): string {
  if (field.kind === 'amount') return formatAmountInput(value);
  if (field.kind === 'integer') return String(value);
  return formatPercentInput(value);
}

function textsOf(values: CalculationAssumptions): Texts {
  const texts = {} as Texts;
  for (const field of FIELDS) texts[field.key] = textOf(field, values[field.key]);
  return texts;
}

function parseText(field: FieldSpec, text: string): number | null {
  if (field.kind === 'amount') return parseAmountInput(text);
  if (field.kind === 'integer') return /^\s*\d+\s*$/.test(text) ? Number(text.trim()) : null;
  return parsePercentInput(text);
}

const MISSING: Record<FieldKind, string> = {
  amount: AMOUNT_MISSING,
  percent: NUMBER_MISSING,
  integer: INTEGER_MISSING,
};

function percentText(pct: number): string {
  return `${formatPercentInput(pct)}\u00a0%`;
}

/** The value as the reset dialog lists it („900 € pro Monat", „4,5 % p. a.", „10 Jahre", „0,60 €/m² mtl."). */
function defaultText(field: FieldSpec, value: number): string {
  if (field.kind === 'amount') {
    return field.section === 'volume'
      ? `${formatEuro(value)} pro Monat`
      : `${formatEuro(value)}${field.suffix.slice(1)}`;
  }
  if (field.kind === 'integer') return `${value} ${field.suffix}`;
  return `${percentText(value)}${field.suffix.slice(1)}`;
}

const thousandths = (value: number) => Math.round(value * 1000);

function sameValues(a: CalculationAssumptions, b: CalculationAssumptions): boolean {
  return FIELDS.every((field) => thousandths(a[field.key]) === thousandths(b[field.key]));
}

/** Parses the texts and validates them with the API's schema: the values or the German message per field. */
function validate(texts: Texts): { values: CalculationAssumptions | null; errors: Partial<Texts> } {
  const errors: Partial<Texts> = {};
  const raw: Partial<Record<FieldKey, number>> = {};
  for (const field of FIELDS) {
    const parsed = parseText(field, texts[field.key]);
    if (parsed === null) errors[field.key] = MISSING[field.kind];
    else raw[field.key] = parsed;
  }
  const result = calculationAssumptionsBodySchema.safeParse(raw);
  if (result.success) return { values: result.data, errors };
  for (const issue of result.error.issues) {
    const key = issue.path[0] as FieldKey | undefined;
    if (key !== undefined && errors[key] === undefined) errors[key] = issue.message;
  }
  return { values: null, errors };
}

function statusOf(current: CalculationAssumptionsView['current']): string | null {
  if (current.isDefault) return null;
  const day = formatDate(current.validFromAt);
  return current.changedBy ? `Angepasst am ${day} von ${current.changedBy.name}` : `Angepasst am ${day}`;
}

const historyColumns: TableColumn<CalculationAssumptionsVersion>[] = [
  { id: 'validFrom', header: 'Gültig ab', track: '150px', cell: (row) => formatDateTime(row.validFromAt) },
  {
    id: 'living',
    header: 'Lebenshaltung',
    track: 'minmax(0,1fr)',
    numeric: true,
    cell: (row) => formatEuro(row.livingExpensesCents),
  },
  {
    id: 'rent',
    header: 'Miete',
    track: '80px',
    numeric: true,
    cell: (row) => percentText(row.rentCreditPct),
  },
  { id: 'cap', header: 'Kappung', track: '80px', numeric: true, cell: (row) => percentText(row.capPct) },
  {
    id: 'interest',
    header: 'Zins',
    track: '80px',
    numeric: true,
    cell: (row) => percentText(row.interestPct),
  },
  {
    id: 'repayment',
    header: 'Tilgung',
    track: '80px',
    numeric: true,
    cell: (row) => percentText(row.repaymentPct),
  },
  {
    id: 'changedBy',
    header: 'Geändert von',
    track: 'minmax(0,1.2fr)',
    cell: (row) => row.changedBy?.name ?? '—',
  },
];

export function CalculationAssumptionsSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: ({ signal }) => api<CalculationAssumptionsView>(PATH, { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
  const data = settings.data;
  const [texts, setTexts] = useState<Texts | null>(null);
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [serverErrors, setServerErrors] = useState<Partial<Texts>>({});
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  // A new version in force (own save, another admin, realtime refetch) replaces the form's texts.
  const versionKey = data ? `${data.current.validFromAt}|${data.history.length}` : null;
  useEffect(() => {
    if (!data) return;
    setTexts(textsOf(data.current));
    setTouched({});
    setServerErrors({});
  }, [versionKey]);

  const checked = useMemo(() => (texts ? validate(texts) : null), [texts]);
  const allowed = data?.actions.update.allowed ?? false;
  const dirty = Boolean(data && checked?.values && !sameValues(checked.values, data.current));
  const invalid = checked !== null && checked.values === null;

  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const exampleCents = useMemo(() => {
    if (!data) return null;
    if (!checked?.values) return data.example.volumeCents;
    return calculateFinancingVolume(CALCULATION_ASSUMPTIONS_EXAMPLE_INPUT, checked.values).volumeCents;
  }, [data, checked]);

  function apply(next: CalculationAssumptionsSaved, message: string) {
    queryClient.setQueryData<CalculationAssumptionsView>(settingsKey, {
      current: next.current,
      defaults: next.defaults,
      history: next.history,
      example: next.example,
      actions: next.actions,
    });
    toast({ message });
  }

  async function save() {
    if (!checked?.values || !dirty || saving || !allowed) return;
    setSaving(true);
    setServerErrors({});
    try {
      apply(
        await api<CalculationAssumptionsSaved>(PATH, { method: 'PUT', body: checked.values }),
        'Gespeichert.',
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setServerErrors(error.fields);
      } else toast({ message: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    if (resetting) return;
    setResetting(true);
    try {
      apply(
        await api<CalculationAssumptionsSaved>(`${PATH}/reset`, { method: 'POST' }),
        'Standardwerte wiederhergestellt.',
      );
      setConfirmReset(false);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setResetting(false);
    }
  }

  const status = data ? statusOf(data.current) : null;

  return (
    <div className={frameStyles.page}>
      <SettingsPageHeader eyebrow="Organisation" title={TITLE} description={INTRO} />
      {settings.isError ? <Banner tone="danger">{errorMessage(settings.error)}</Banner> : null}
      {settings.isPending ? (
        <Card className={styles.skeletonCard}>
          <Skeleton width="30%" height={16} />
          <Skeleton variant="block" height={44} />
          <Skeleton variant="block" height={44} />
          <Skeleton variant="block" height={44} />
        </Card>
      ) : null}
      {data && texts ? (
        <>
          <section className={styles.card} aria-label={TITLE}>
            <div className={styles.cardHead}>
              {status === null ? (
                <Pill variant="neutral" size="large">
                  Standardwerte
                </Pill>
              ) : (
                <span className={styles.status}>{status}</span>
              )}
            </div>
            {!allowed && data.actions.update.reason ? (
              <div className={styles.reason}>
                <Banner tone="info">{data.actions.update.reason}</Banner>
              </div>
            ) : null}
            {SECTIONS.map((section) => (
              <div key={section.key} className={styles.section}>
                <div className={styles.sectionHead}>
                  <h3 className={styles.sectionTitle}>{section.title}</h3>
                  {section.intro ? <p className={styles.sectionIntro}>{section.intro}</p> : null}
                </div>
                <div className={styles.fields}>
                  {FIELDS.filter((field) => field.section === section.key).map((field) => {
                    const error = touched[field.key]
                      ? (checked?.errors[field.key] ?? serverErrors[field.key])
                      : serverErrors[field.key];
                    return (
                      <TextField
                        key={field.key}
                        label={field.label}
                        inputMode={field.kind === 'integer' ? 'numeric' : 'decimal'}
                        value={texts[field.key]}
                        readOnly={!allowed}
                        error={error}
                        trailing={<span className={styles.suffix}>{field.suffix}</span>}
                        onChange={(event) => {
                          setTexts({ ...texts, [field.key]: event.target.value });
                          setTouched({ ...touched, [field.key]: true });
                        }}
                        onBlur={() => {
                          if (field.kind !== 'amount') return;
                          const cents = parseAmountInput(texts[field.key]);
                          if (cents !== null) setTexts({ ...texts, [field.key]: formatAmountInput(cents) });
                        }}
                      />
                    );
                  })}
                </div>
                {section.key === 'volume' ? (
                  <p className={styles.example} aria-live="polite">
                    Beispiel: <span className={styles.exampleAmount}>{formatEuro(exampleCents ?? 0)}</span>{' '}
                    Finanzierungsvolumen bei {formatEuro(data.example.netIncomeCents)} Nettoeinkommen und{' '}
                    {formatEuro(data.example.grossRentCents)} Warmmiete.
                  </p>
                ) : null}
              </div>
            ))}
            {allowed ? (
              <div className={styles.footer}>
                {data.current.isDefault ? null : (
                  <Button variant="secondary" onClick={() => setConfirmReset(true)}>
                    Auf Standardwerte zurücksetzen
                  </Button>
                )}
                <Button loading={saving} disabled={!dirty || invalid} onClick={() => void save()}>
                  Speichern
                </Button>
              </div>
            ) : null}
          </section>

          {data.history.length > 0 ? (
            <section className={styles.card} aria-labelledby="assumptions-history-title">
              <div className={styles.cardHead}>
                <h3 id="assumptions-history-title" className={styles.cardTitle}>
                  Bisherige Versionen
                </h3>
              </div>
              <div className={styles.historyTable}>
                <Table
                  className={styles.historyInner}
                  label="Bisherige Versionen"
                  columns={historyColumns}
                  rows={data.history}
                  rowKey={(row) => row.validFromAt}
                  density="compact"
                />
              </div>
            </section>
          ) : null}

          <Dialog
            open={confirmReset}
            onClose={() => setConfirmReset(false)}
            width={560}
            title="Standardwerte wiederherstellen?"
            footer={
              <>
                <Button variant="secondary" onClick={() => setConfirmReset(false)} disabled={resetting}>
                  Abbrechen
                </Button>
                <Button loading={resetting} onClick={() => void reset()}>
                  Zurücksetzen
                </Button>
              </>
            }
          >
            <ul className={styles.defaultsList}>
              {FIELDS.map((field) => (
                <li key={field.key}>
                  {field.label}:{' '}
                  <span className={styles.defaultsValue}>{defaultText(field, data.defaults[field.key])}</span>
                </li>
              ))}
            </ul>
          </Dialog>
        </>
      ) : null}
    </div>
  );
}
